import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import { DrawerActions, useIsFocused, useNavigation, useNavigationState, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Feather } from '@expo/vector-icons';

import Logo from '../../components/logo/Logo';
import Text from '../../components/text/Text';
import { ScrollView as WebSafeScrollView } from '../../components/ScrollView';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useBackLabel from '../../hooks/useBackLabel';
import { clearScreenFooterHeight, setScreenFooterHeight, useSyncBannerHeight } from '../screenFooterHeight';
import { TabPagesContext } from '../tabPages';
import EmptyState from './EmptyState';
import ProgressBar from './ProgressBar';

/**
 * Screen: page frame for redesigned screens. Order from top: header, scrolling
 * body, banner slot, ActionDock. Register the route with
 * `options={{ headerShown: false }}` because Screen draws its own header.
 * Props:
 * Header
 * - title: centred small title with a back button ("Stop 4 of 13"); without a
 *   back button it is the left-aligned page name. Home and the bottom bar pages
 *   (no Back button there) show the Sentry logo when they have no title.
 * - back button: always reads "Back". backLabel (e.g. "Run"; default: the previous screen's label from useBackLabel)
 *   only names the destination for screen readers ("Back to Run"). onBack (default navigation.goBack), showBack (default: can go back)
 * - headerRight: node before the menu button (StatusBadge "Clocked in 6:48 am", "Step 5 of 8")
 * - showMenu (default true): hamburger that opens the side menu
 * - headerProgress (0..1): thin progress bar under the header (stop screen)
 * Body
 * - heading, subheading: page title block at the top of the body
 * - children; scroll (default true; false for screens that bring their own list)
 * - scrollEnabled (default true): false holds the page still (while dragging a row)
 * - refreshControl, keyboardShouldPersistTaps (default 'handled'), contentContainerStyle
 * - isLoading: spinner in place of the body; error (string) + onRetry: error state with "Try again"
 *   (dock is hidden while loading or in error)
 * Footer
 * - banner: slot directly above the dock (TrainingModeBanner, "Saved on phone, will sync" notes).
 *   The floating SyncStatusBanner is lifted above the whole footer automatically, and the
 *   body gets matching bottom padding while it shows.
 * - dock: usually <ActionDock />
 * - style
 */
const Screen = ({
	title,
	backLabel: backLabelProp,
	onBack,
	showBack,
	headerRight,
	showMenu = true,
	headerProgress,
	heading,
	subheading,
	children,
	scroll = true,
	scrollEnabled = true,
	refreshControl,
	keyboardShouldPersistTaps = 'handled',
	contentContainerStyle,
	isLoading = false,
	error,
	onRetry,
	banner,
	dock,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const navigation = useNavigation();
	const route = useRoute();
	const parentBackLabel = useBackLabel();
	const backLabel = backLabelProp ?? parentBackLabel;
	const isFocused = useIsFocused();
	const insets = useSafeAreaInsets();
	const [footerHeight, setFooterHeight] = useState(0);
	const syncBannerHeight = useSyncBannerHeight();
	// The floating sync banner sits 12 pt above the footer, over the end of the body:
	// add its height and gap to the body's bottom padding so the last content scrolls clear.
	const withSyncBannerSpace = (bodyStyle) => {
		if (!syncBannerHeight) {
			return bodyStyle;
		}
		const basePadding = StyleSheet.flatten(bodyStyle)?.paddingBottom || 0;
		return [bodyStyle, { paddingBottom: basePadding + syncBannerHeight + 12 }];
	};
	// canGoBack() is not reactive: a screen with no later re-render (e.g. Settings) would keep
	// its first-render value, taken before the stack counted it. Subscribe to this route's position.
	const tabPages = useContext(TabPagesContext);
	const routeIndex = useNavigationState(state => state?.routes?.findIndex(item => item.key === route.key) ?? 0);
	const hasStackHistory = routeIndex > 0;
	// Bottom bar pages are equal: opened from Home or the bar (directly above Home) they get no Back.
	const isTabPage = routeIndex <= 1 && tabPages.has(route.name);
	const canGoBack = showBack ?? (!isTabPage && (hasStackHistory || navigation.canGoBack()));
	const hasError = Boolean(error);
	const isDockVisible = Boolean(dock) && !isLoading && !hasError;

	// Only the focused screen lifts the sync banner; stacked screens stay mounted.
	useEffect(() => {
		if (isFocused) {
			setScreenFooterHeight(route.key, footerHeight);
		} else {
			clearScreenFooterHeight(route.key);
		}
	}, [isFocused, footerHeight, route.key]);
	useEffect(() => () => clearScreenFooterHeight(route.key), [route.key]);

	const handleFooterLayout = useCallback((event) => {
		setFooterHeight(event.nativeEvent.layout.height);
	}, []);
	const handleBack = useCallback(() => {
		if (onBack) {
			onBack();
			return;
		}
		navigation.goBack();
	}, [navigation, onBack]);
	const handleMenu = useCallback(() => {
		navigation.dispatch(DrawerActions.toggleDrawer());
	}, [navigation]);

	const renderBody = () => {
		const titleBlock = Boolean(heading || subheading) && (
			<View style={styles.headingBlock}>
				{Boolean(heading) && <Text style={styles.heading} accessibilityRole="header">{heading}</Text>}
				{Boolean(subheading) && <Text style={styles.subheading}>{subheading}</Text>}
			</View>
		);
		const staticTitleBlock = Boolean(titleBlock) && <View style={styles.staticHeading}>{titleBlock}</View>;

		// Loading and error keep the page heading so the person still sees where they are.
		if (isLoading || hasError) {
			return (
				<View style={styles.staticBody}>
					{staticTitleBlock}
					{isLoading ? (
						<View style={styles.centered} accessibilityLabel="Loading">
							<ActivityIndicator size="large" color={colors.accent} />
						</View>
					) : (
						<View style={styles.centered}>
							<EmptyState
								tone="error"
								title="Something went wrong"
								message={typeof error === 'string' ? error : 'Check your connection and try again.'}
								actionLabel={onRetry ? 'Try again' : undefined}
								onAction={onRetry}
							/>
						</View>
					)}
				</View>
			);
		}

		if (!scroll) {
			return (
				<View style={withSyncBannerSpace([styles.staticBody, contentContainerStyle])}>
					{staticTitleBlock}
					{children}
				</View>
			);
		}

		const ScrollComponent = Platform.OS === 'web' ? WebSafeScrollView : KeyboardAwareScrollView;
		const keyboardProps = Platform.OS === 'web' ? {} : { enableOnAndroid: true, extraScrollHeight: 16 };

		return (
			<ScrollComponent
				style={styles.scroll}
				// Flattened: on Android KeyboardAwareScrollView reads paddingBottom off a plain
				// object only and otherwise replaces it with 0 (no bottom space, sync banner overlap).
				contentContainerStyle={StyleSheet.flatten(withSyncBannerSpace([styles.scrollContent, contentContainerStyle]))}
				refreshControl={refreshControl}
				scrollEnabled={scrollEnabled}
				keyboardShouldPersistTaps={keyboardShouldPersistTaps}
				scrollIndicatorInsets={{ right: 1 }}
				{...keyboardProps}
			>
				{titleBlock}
				{children}
			</ScrollComponent>
		);
	};

	return (
		<View style={[styles.screen, style]}>
			<View style={[styles.header, { paddingTop: insets.top }]}>
				<View style={styles.headerRow}>
					<View style={[styles.headerSide, styles.headerSideLeft]}>
						{canGoBack ? (
							<Pressable
								onPress={handleBack}
								accessibilityRole="button"
								accessibilityLabel={backLabel ? `Back to ${backLabel}` : 'Back'}
								hitSlop={4}
								style={({ pressed }) => [styles.backButton, pressed && styles.pressed]}
							>
								<Feather name="chevron-left" size={22} color={colors.accent} />
								{/* Always "Back": a screen name such as "Stop" read like an action to drivers. */}
								<Text style={styles.backLabel} numberOfLines={1}>Back</Text>
							</Pressable>
						) : title ? (
							<Text style={styles.pageName} accessibilityRole="header" numberOfLines={1}>{title}</Text>
						) : (
							(isTabPage || routeIndex === 0) && <Logo style={styles.logo} />
						)}
					</View>
					{canGoBack && Boolean(title) ? (
						<Text style={styles.headerTitle} accessibilityRole="header" numberOfLines={1}>{title}</Text>
					) : (
						<View style={styles.headerSpacer} />
					)}
					<View style={[styles.headerSide, styles.headerSideRight]}>
						{/* Wrapped so children with alignSelf (StatusBadge) still centre vertically. */}
						{Boolean(headerRight) && <View>{headerRight}</View>}
						{showMenu && (
							<Pressable
								onPress={handleMenu}
								accessibilityRole="button"
								accessibilityLabel="Menu"
								style={({ pressed }) => [styles.menuButton, pressed && styles.pressed]}
							>
								<Feather name="menu" size={22} color={colors.icon} />
							</Pressable>
						)}
					</View>
				</View>
			</View>
			{headerProgress !== undefined && headerProgress !== null && (
				<ProgressBar value={headerProgress} variant="thin" />
			)}
			<View style={styles.body}>
				{renderBody()}
			</View>
			<View onLayout={handleFooterLayout}>
				{banner}
				{isDockVisible && dock}
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, layout } }) => ({
	screen: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	header: {
		backgroundColor: colors.surface,
	},
	headerRow: {
		height: layout.headerHeight,
		flexDirection: 'row',
		alignItems: 'center',
		paddingLeft: 6,
		paddingRight: 6,
	},
	// Sides size to their content (at least 88 so a short title stays centred);
	// the title takes the space between them and truncates, never overlapping.
	headerSide: {
		minWidth: 88,
		flexShrink: 0,
		flexDirection: 'row',
		alignItems: 'center',
	},
	headerSideLeft: {
		flexShrink: 1,
	},
	headerSideRight: {
		justifyContent: 'flex-end',
		gap: space.xs,
	},
	headerSpacer: {
		flex: 1,
	},
	backButton: {
		minHeight: 44,
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		paddingHorizontal: 10,
	},
	backLabel: {
		...type.rowTitle,
		color: colors.accent,
		flexShrink: 1,
	},
	pageName: {
		...type.sectionTitle,
		letterSpacing: 0.32,
		color: colors.text,
		paddingLeft: space.md,
	},
	// 1000 x 123 logotype at 20 high.
	logo: {
		width: 163,
		height: 20,
		marginLeft: space.md,
	},
	headerTitle: {
		...type.bodySmallStrong,
		color: colors.text,
		textAlign: 'center',
		flex: 1,
		paddingHorizontal: space.xs,
	},
	menuButton: {
		width: 44,
		height: 44,
		alignItems: 'center',
		justifyContent: 'center',
	},
	pressed: {
		opacity: 0.7,
	},
	body: {
		flex: 1,
		minHeight: 0,
	},
	scroll: {
		flex: 1,
	},
	scrollContent: {
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.lg,
		gap: space.gutter,
	},
	staticBody: {
		flex: 1,
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
	},
	staticHeading: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.md,
	},
	headingBlock: {
		gap: 6,
	},
	heading: {
		...type.title,
		color: colors.text,
	},
	subheading: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	centered: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		padding: space.xl,
	},
});

export default Screen;
